import { apiClient } from './client';
import { IntakeRunsListSchema, type IntakeRunsList } from './requirement-library-intake';

/**
 * The app-wide intake run watcher's endpoint (T-0157).
 *
 * `GET /api/v1/requirement-library/intake/runs?mine=1&status=active
 * [&changed_since=<ISO>]` answers with the caller's own runs still being
 * read, plus any of theirs that changed since `changed_since` (so a run
 * that went from extracting to failed between two looks is still seen).
 * `server_time` is the server clock to send back next time.
 *
 * Contract: `docs/requirement-library/technical/api-requirement-library-intake.md`.
 */

const RUNS = '/api/v1/requirement-library/intake/runs';

/**
 * How far before the last `server_time` the next `changed_since` reaches.
 * Run changes are stamped by the API and worker clocks, which may drift a
 * little from each other; looking back a minute costs a few repeated rows,
 * which the watcher already ignores.
 */
export const CHANGED_SINCE_OVERLAP_MS = 60_000;

export function listMyActiveIntakeRuns(changedSince: string | null): Promise<IntakeRunsList> {
  return apiClient(RUNS, IntakeRunsListSchema, {
    query: {
      mine: 1,
      status: 'active',
      limit: 100,
      ...(changedSince ? { changed_since: changedSince } : {}),
    },
  });
}

/**
 * Delay before look number `attempt` (from 0) while a run is still being
 * read: quick at first, since a short document is read in seconds and a
 * failure should be seen at once, then backing off to every 30 seconds for
 * a long read or one waiting on the recovery sweep.
 */
export const WATCH_DELAYS_MS = [3_000, 5_000, 8_000, 13_000, 20_000, 30_000] as const;

export function watchDelayMs(attempt: number): number {
  return WATCH_DELAYS_MS[Math.min(Math.max(0, attempt), WATCH_DELAYS_MS.length - 1)]!;
}
